import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Divider,
  useMediaQuery,
  Badge,
  Tooltip,
  Fab,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Pending as PendingIcon,
  History as HistoryIcon,
  Logout as LogoutIcon,
  MovieFilter as MovieIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Telegram as TelegramIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemModeIcon,
  Add as AddIcon,
  Storage as StorageIcon,
  PlaylistPlay as PlaylistPlayIcon,
  PieChart as PieChartIcon,
  MonitorHeart as MonitorHeartIcon,
  CalendarMonth as CalendarIcon,
  Subtitles as SubtitlesIcon,
  FormatSize as FormatSizeIcon,
  AutoAwesome as AutoAwesomeIcon,
  DriveFolderUpload as ImportIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme, UI_SIZES } from '../contexts/ThemeContext';
import AddTorrentDialog from './AddTorrentDialog';

const drawerWidth = 280;
const MotionBox = motion(Box);

function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [addTorrentOpen, setAddTorrentOpen] = useState(false);
  const { user, logout } = useAuth();
  const { mode, themePreference, setThemeMode, cycleTheme, uiSize, cycleUiSize } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  // Get the appropriate icon for the current theme preference
  const getThemeIcon = () => {
    if (themePreference === 'system') return <SystemModeIcon />;
    if (themePreference === 'light') return <LightModeIcon />;
    return <DarkModeIcon />;
  };

  const getThemeTooltip = () => {
    if (themePreference === 'system') return 'System Theme (click to cycle)';
    if (themePreference === 'light') return 'Light Mode (click to cycle)';
    return 'Dark Mode (click to cycle)';
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
    { path: '/pending', label: 'Pending Operations', icon: <PendingIcon /> },
    { path: '/import', label: 'Import from Disk', icon: <ImportIcon /> },
    { path: '/history', label: 'History', icon: <HistoryIcon /> },
    { path: '/torrents', label: 'Torrents', icon: <StorageIcon /> },
    { path: '/watchlist', label: 'Watchlist', icon: <PlaylistPlayIcon /> },
    { path: '/discover', label: 'Discover', icon: <AutoAwesomeIcon /> },
    { path: '/calendar', label: 'Release Calendar', icon: <CalendarIcon /> },
    { path: '/subtitles', label: 'Subtitles', icon: <SubtitlesIcon /> },
    { path: '/storage', label: 'Storage Analytics', icon: <PieChartIcon /> },
    { path: '/health', label: 'System Health', icon: <MonitorHeartIcon /> },
    { path: '/settings', label: 'Settings', icon: <SettingsIcon /> },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3 }}>
        <MotionBox
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
        >
          <MovieIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold" color="primary.light">
              FlickHub
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Professional Media Organizer
            </Typography>
          </Box>
        </MotionBox>
      </Box>
      
      <Divider sx={{ borderColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)' }} />
      
      <List sx={{ flexGrow: 1, px: 2, py: 2 }}>
        {menuItems.map((item, index) => (
          <motion.div
            key={item.path}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'white',
                      fontWeight: 600,
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.95rem',
                    fontWeight: location.pathname === item.path ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          </motion.div>
        ))}
      </List>
      
      <Divider sx={{ borderColor: mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)' }} />
      
      {/* Theme Toggle */}
      <Box sx={{ px: 2, py: 1 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Appearance
          </Typography>
          <ToggleButtonGroup
            value={themePreference}
            exclusive
            onChange={(e, newMode) => newMode && setThemeMode(newMode)}
            size="small"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                flex: 1,
                py: 1,
                borderColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              },
            }}
          >
            <ToggleButton value="light">
              <Tooltip title="Light Mode">
                <LightModeIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="system">
              <Tooltip title="System">
                <SystemModeIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="dark">
              <Tooltip title="Dark Mode">
                <DarkModeIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          {themePreference === 'system' && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Following system ({mode})
            </Typography>
          )}
        </Box>
      </Box>
      
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TelegramIcon sx={{ fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight="bold" color="white">
              Telegram Connected
            </Typography>
          </Box>
          <Typography variant="caption" color="rgba(255, 255, 255, 0.9)">
            Receive notifications and approve operations via Telegram
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            {user?.username?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {user?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Administrator
            </Typography>
          </Box>
        </Box>
        
        <Button
          fullWidth
          variant="outlined"
          color="error"
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          sx={{ borderRadius: 2 }}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: mode === 'dark' 
            ? 'rgba(10, 10, 10, 0.8)' 
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: mode === 'dark' 
            ? '1px solid rgba(255, 255, 255, 0.05)' 
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            {menuItems.find(item => item.path === location.pathname)?.label || 'FlickHub'}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Notifications">
              <IconButton color="inherit" size="small">
                <Badge badgeContent={0} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            
            <Tooltip title={`Interface size: ${UI_SIZES[uiSize].label} — click to change`}>
              <IconButton color="inherit" size="small" onClick={cycleUiSize}>
                <FormatSizeIcon sx={{ fontSize: uiSize === 'compact' ? 17 : uiSize === 'medium' ? 20 : 24 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Settings">
              <IconButton color="inherit" size="small" onClick={() => navigate('/settings')}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={getThemeTooltip()}>
              <IconButton color="inherit" size="small" onClick={cycleTheme}>
                {getThemeIcon()}
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: mode === 'dark' ? '#171717' : '#ffffff',
              borderRight: mode === 'dark' 
                ? '1px solid rgba(255, 255, 255, 0.05)' 
                : '1px solid rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: mode === 'dark' ? '#171717' : '#ffffff',
              borderRight: mode === 'dark' 
                ? '1px solid rgba(255, 255, 255, 0.05)' 
                : '1px solid rgba(0, 0, 0, 0.1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar />
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Outlet />
        </Box>
      </Box>

      <Tooltip title="Add Torrent">
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => setAddTorrentOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            background: (theme) => theme.palette.custom.gradients.primary,
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      <AddTorrentDialog
        open={addTorrentOpen}
        onClose={() => setAddTorrentOpen(false)}
      />
    </Box>
  );
}

export default Layout;